import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  SearchSelect,
  Select,
  Spinner,
} from '@wundo/ui';
import { fetchCohort } from '@/lib/cohorts.ts';
import { searchOrganisationCohorts } from '@/lib/organisations.ts';
import {
  fetchGuestVisitSettings,
  startGuestVisit,
  type GuestVisitSettings,
} from '@/lib/impersonation.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import type { Cohort } from '@/types/cohorts.ts';

const ROLE_OPTIONS = [
  { value: 'user', label: 'Member' },
  { value: 'admin', label: 'Admin' },
];

export interface GuestVisitDialogProps {
  organisationId: string;
  organisationName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Work in a customer organisation as yourself: through your own guest account
 * there, with the role and cohort picked here. Opens the customer app in a new
 * tab, like "Login as".
 */
export function GuestVisitDialog({
  organisationId,
  organisationName,
  open,
  onOpenChange,
}: GuestVisitDialogProps) {
  const { data: settings, isError } = useQuery({
    queryKey: ['guestVisitSettings', organisationId],
    queryFn: () => fetchGuestVisitSettings(organisationId),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>Work in {organisationName}</DialogTitle>
        </DialogHeader>
        {isError && (
          <p role='alert' className='px-5 py-4 text-sm text-danger'>
            Failed to load your guest account
          </p>
        )}
        {!settings && !isError && (
          <p className='flex items-center gap-2 px-5 py-4 text-sm font-semibold text-ink-muted'>
            <Spinner />
            Loading…
          </p>
        )}
        {/* Mounted once the settings are in, so the fields open on them. */}
        {open && settings && (
          <GuestVisitForm
            organisationId={organisationId}
            initial={settings}
            onCancel={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function GuestVisitForm({
  organisationId,
  initial,
  onCancel,
}: {
  organisationId: string;
  initial: GuestVisitSettings;
  onCancel: () => void;
}) {
  const [role, setRole] = useState(initial.role);
  const [cohortId, setCohortId] = useState(initial.cohort_id);
  const [pickedCohort, setPickedCohort] = useState<Cohort | null>(null);

  const { data: initialCohort } = useQuery({
    queryKey: ['cohort', initial.cohort_id],
    queryFn: () => fetchCohort(initial.cohort_id as string),
    enabled: initial.cohort_id !== null,
  });
  const cohort =
    [pickedCohort, initialCohort].find((one) => one != null && one.id === cohortId) ??
    null;

  /* It reloads this tab when it works, so only a failure is ever shown. */
  const visit = useMutation({
    mutationFn: () => startGuestVisit(organisationId, { role, cohort_id: cohortId }),
  });

  return (
    <>
      <div className='flex flex-col gap-4 px-5 py-4'>
        <p className='text-sm text-ink-muted'>
          You work here through your own guest account. It never counts in this
          organisation&apos;s figures, and the visit ends an hour after your last click,
          at the latest after eight hours.
        </p>
        <Field htmlFor='guest-visit-role' label='Role'>
          <Select
            id='guest-visit-role'
            wrapperClassName='w-full'
            className='w-full'
            options={ROLE_OPTIONS}
            value={role}
            onChange={(event) =>
              setRole(event.target.value === 'admin' ? 'admin' : 'user')
            }
          />
        </Field>
        <Field htmlFor='guest-visit-cohort' label='Cohort'>
          <SearchSelect
            id='guest-visit-cohort'
            clearable
            placeholder='No cohort'
            value={cohort}
            loadOptions={(query, signal) =>
              searchOrganisationCohorts(organisationId, query, signal)
            }
            getKey={(one: Cohort) => one.id}
            getLabel={(one: Cohort) => one.title}
            onChange={(one) => {
              setPickedCohort(one);
              setCohortId(one?.id ?? null);
            }}
          />
        </Field>
        {visit.isError && (
          <p role='alert' className='text-sm text-danger'>
            {apiErrorMessage(visit.error, 'Failed to start working in this organisation')}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button size='sm' variant='quiet' disabled={visit.isPending} onClick={onCancel}>
          Cancel
        </Button>
        <Button size='sm' loading={visit.isPending} onClick={() => visit.mutate()}>
          Open the app
        </Button>
      </DialogFooter>
    </>
  );
}
